import { IItemCache } from '../../models/IItemCacheObserver';
import {
    displayNameFacet,
    fileNameFacet,
    folderFacet,
    fileSizeFacet,
    dateModifiedFacet,
    modifiedByPersonFacet,
    missingMetadataFacet,
    policyTipFacet,
    isCheckedOutFacet,
    malwareDetectedFacet,
    retentionPolicyFacet,
    isSharedFacet,
    isNewFacet
} from '../../facets/ItemFacets';
import { PolicyTipType, RetentionPolicyType } from '../../models/ItemPolicy';
import * as StringHelper from '@ms/utilities-strings/lib/index';
import DriveSpaceHelper from '@ms/items-view-utilities/lib/string/DriveSpaceHelper';
import strings from './ItemStrings.resx';
import fieldStrings from '../../components/fields/FieldStrings.resx';
import { countFacet } from '../../facets/GroupFacets';
/**
 * Creates the default aria label for a row. Examples:
 * testfolder, Folder, Modified 3/28/2018, edited by Amy Smith
 * puppy.PNG, png File, Modified 4/3/2018, edited by Katherine Johnson, 8.78 KB
 */
export function getRowAriaLabel(itemCache: IItemCache, props: {
    itemKey: string;
}): string {
    const {
        itemKey
    } = props;

    const {
        displayName,
        folder,
        dateModified,
        modifiedByPerson,
        missingMetadata,
        policyTip,
        isCheckedOut,
        isMalwareDetected,
        retentionPolicy,
        count,
        isShared,
        isNew
    } = itemCache.demandItem({
        displayName: displayNameFacet,
        folder: folderFacet,
        dateModified: dateModifiedFacet,
        modifiedByPerson: modifiedByPersonFacet,
        missingMetadata: missingMetadataFacet,
        policyTip: policyTipFacet,
        isCheckedOut: isCheckedOutFacet,
        isMalwareDetected: malwareDetectedFacet,
        retentionPolicy: retentionPolicyFacet,
        count: countFacet,
        isShared: isSharedFacet,
        isNew: isNewFacet
    }, itemKey);

    const isDefinitelyNotAFolder = itemCache.isItemFacetResolved(folderFacet, itemKey) && !folder;

    const fileSize = isDefinitelyNotAFolder ?
        itemCache.demandItemFacet(fileSizeFacet, itemKey) :
        undefined;

    const fileName = isDefinitelyNotAFolder ?
        itemCache.demandItemFacet(fileNameFacet, itemKey) :
        undefined;

    const parts: string[] = [];

    if (displayName) {
        parts.push(displayName);
    }

    if (folder) {
        parts.push(strings.fileTypeFolder);
        if (count) {
            parts.push(StringHelper.format(strings.folderItemCount, count));
        }
        if (isShared) {
            parts.push(strings.folderIsShared);
        }
    } else if (fileName && fileName.extension) {
        const fileExtension = fileName.extension;
        const fileType = fileExtension[0] === '.' ? fileExtension.substring(1) : fileExtension;
        parts.push(`${fileType} ${strings.fileTypeNoExtension}`);
    }

    if (isNew) {
        parts.push(fieldStrings.itemSignalNew);
    }

    if (isCheckedOut) {
        if (isCheckedOut.byYou) {
            parts.push(fieldStrings.itemSignalYouCheckedOut);
        } else if (!!isCheckedOut.byOther) {
            const userFacets = itemCache.demandItem({
                displayName: displayNameFacet
            }, isCheckedOut.byOther);

            const titleString = userFacets && userFacets.displayName ?
                StringHelper.format(fieldStrings.itemSignalSomeoneCheckedOutTooltip, userFacets.displayName) :
                fieldStrings.itemSignalSomeoneCheckedOut;

            parts.push(titleString);
        }
    }

    if (isMalwareDetected) {
        parts.push(fieldStrings.itemSignalMalwareDetected);
    }

    if (policyTip) {
        if (policyTip === PolicyTipType.block || policyTip === PolicyTipType.notifyAndBlock) {
            parts.push(strings.policyTipBlockText);
        } else if (policyTip === PolicyTipType.notify) {
            parts.push(strings.policyTipNotifyText);
        }
    }

    if (retentionPolicy && retentionPolicy === RetentionPolicyType.record) {
        parts.push(fieldStrings.itemSignalIsRecord);
    }

    if (missingMetadata) {
        parts.push(strings.missingMetadataText);
    }

    if (dateModified) {
        const friendlyDate = new Date(dateModified).toLocaleDateString();
        parts.push(StringHelper.format(strings.dateModifiedLabelFormat, friendlyDate));
    }

    if (modifiedByPerson) {
        const userFacets = itemCache.demandItem({
            displayName: displayNameFacet,
        }, modifiedByPerson.itemKey);
        if (userFacets && userFacets.displayName) {
            parts.push(StringHelper.format(strings.gridViewSubTitle, userFacets.displayName));
        }
    }

    if (fileSize) {
        parts.push(DriveSpaceHelper.getDisplayString(fileSize, { ignoreZero: true }));
    }

    return parts.join(', ');
}

export function getRowCheckboxAriaLabel(itemCache: IItemCache, props: { itemKey: string }): string {
    const {
        displayName,
        fileName
    } = itemCache.demandItem({
        displayName: displayNameFacet,
        fileName: fileNameFacet
    }, props.itemKey);

    const parts: string[] = [];

    if (displayName) {
        parts.push(displayName);
    } else if (fileName) {
        parts.push(fileName.name);
    }

    return StringHelper.format(strings.checkboxAriaLabel, parts[0]);
}
